<script lang="ts">
	//Services
	import { postReq } from '../../../services/http';
	import { format } from '../../../services/i18n';
	import { user_data } from 'src/stores/account/user';
	//Helpers
	import { capFirstChar } from '../../../helpers/utilities';
	//Design system components
	import {
		Collapse,
		toast,
		Loader,
		Stack,
		SelectCard,
		IconButton,
		CardGrid,
		Modal,
		ButtonV2,
		Text,
		CircleAvatar,
		AlertV2,
		Container,
	} from '@Shelly/shelly-design-system';
	import { deleteImageFromStore, handleRequest } from 'src/helpers/upload_image';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	const userImagesPath = '/shelly_files/assets/user_images/';

	const imageGalleries = [
		{ home: $format('tab_home', { default: 'Home' }) },
		{ device: $format('tab_devices', { default: 'Devices' }) },
		{ room: $format('tab_rooms', { default: 'Rooms' }) },
		{ group: $format('tab_groups', { default: 'Groups' }) },
		{ scene: $format('tab_scenes', { default: 'Scenes' }) },
	];

	$: galleriesKeys = [
		$format('tab_home', { default: 'Home' }),
		$format('tab_devices', { default: 'Devices' }),
		$format('tab_rooms', { default: 'Rooms' }),
		$format('tab_groups', { default: 'Groups' }),
		$format('tab_scenes', { default: 'Scenes' }),
	];

	const removeImageDataReset: {
		image?: { filepath: string; thumb?: string };
		galleryIndex: number | null;
		galleryType?: Record<string, string>;
	} = {
		galleryIndex: null,
	};

	let showModal = false;
	let galleryCollapses = [...imageGalleries.map((e) => Object.values(e)[0])].map((galleryName) => {
		return {
			title: galleryName,
			expanded: false,
			list: [],
			isLoading: false,
		};
	});

	let removeImageData = {
		...removeImageDataReset,
	};

	const closeModal = () => {
		showModal = false;
	};

	const clearSelection = () => {
		removeImageData = {
			...removeImageDataReset,
		};
	};

	async function removeImage() {
		if (!removeImageData.image || removeImageData.galleryIndex === null || !removeImageData.galleryType) {
			return;
		}

		const imgPath = removeImageData.image.filepath;
		const galleryIndex = removeImageData.galleryIndex;
		const galleryType = removeImageData.galleryType;

		closeModal();

		const res = await postReq('/user_files/delete_image', {
			img_path: imgPath,
		});
		if (res.isok) {
			toast.open({
				type: 'success',
				title: $format('toast_title_picture_deleted', {
					default: 'Picture deleted successfully.',
				}),
			});

			// Ivo: Deleting profile image helper.
			deleteImageFromStore(removeImageData);

			// Update gallery localy first, to remove image instantly
			galleryCollapses[galleryIndex] = {
				...galleryCollapses[galleryIndex],
				list: galleryCollapses[galleryIndex].list.filter((img) => img.filepath !== imgPath),
			};

			let imgList = await handleRequest(Object.keys(galleryType)[0]);

			galleryCollapses[galleryIndex] = {
				...galleryCollapses[galleryIndex],
				list: imgList,
			};

			const imgUrl = $user_data.user_api_url + userImagesPath + imgPath;

			window.URL.revokeObjectURL(imgUrl);

			const imgThumbPath = removeImageData.image?.thumb;

			if (imgThumbPath) {
				const imgThumbUrl = $user_data.user_api_url + userImagesPath + imgThumbPath;

				window.URL.revokeObjectURL(imgThumbUrl);
			}
		}

		clearSelection();
	}

	const onExpandCollapse = async (index: number) => {
		const galleryItem = galleryCollapses[index];
		let expanded = !galleryItem.expanded;
		galleryCollapses[index] = {
			...galleryItem,
			expanded: expanded,
			isLoading: expanded,
		};

		if (expanded) {
			// Get gallery images on expand
			let imgList = await handleRequest(Object.keys(imageGalleries[index])[0]);
			galleryCollapses[index] = {
				...galleryCollapses[index],
				isLoading: false,
				list: imgList,
			};
		}
	};

	const onDeleteImage = (image, index) => {
		showModal = true;
		removeImageData = {
			image: image,
			galleryType: imageGalleries[index],
			galleryIndex: index,
		};
	};
</script>

<Stack gap="s">
	{#each galleryCollapses as gallery, i}
		{@const galleryKey = Object.keys(imageGalleries[i] ?? {})[0]}
		<Collapse
			backgroundColor="color-black"
			id={`collapse-gallery-${galleryKey}`}
			title={capFirstChar(galleriesKeys[i])}
			expanded={gallery.expanded}
			on:click={() => {
				onExpandCollapse(i);
			}}
		>
			{#if gallery.isLoading}
				<div class="d-flex justify-content-center">
					<Loader size="m" />
				</div>
			{:else if gallery.expanded && !gallery.isLoading}
				{#if gallery.list.length > 0}
					<CardGrid sm={80} noPaddingBottom>
						{#each gallery.list as g}
							<SelectCard
								cardBgColor={$showNewLayout ? 'color-1' : undefined}
								image={$user_data.user_api_url + userImagesPath + g.filepath}
								size="s"
							>
								<IconButton
									aria-label={$format('button_delete_image', {
										default: 'Delete image',
									})}
									slot="customAction"
									icon="fas fa-trash"
									variant="danger"
									on:click={() => {
										onDeleteImage(g, i);
									}}
								/>
							</SelectCard>
						{/each}
					</CardGrid>
				{:else}
					<AlertV2
						backgroundColor="color-00"
						heading={$format('no_images', { default: 'No Images' })}
					/>
				{/if}
			{/if}
		</Collapse>
	{/each}
</Stack>
<Modal
	{showModal}
	title={$format('delete_image_title', { default: 'Delete Image?' })}
	on:close={closeModal}
	class="justify-content-center "
>
	<svelte:fragment slot="content">
		<Container padding="0">
			<div class="d-flex justify-content-center mb-m">
				<CircleAvatar
					image={$user_data.user_api_url + userImagesPath + removeImageData.image?.filepath}
					size="xl"
				/>
			</div>
			<Text>
				{$format('delete_img_msg', {
					default:
						'Deleting this image will permanently remove it from your account. Once deleted, you won’t be able to find it in your uploaded pictures.',
				})}
			</Text>
		</Container>
	</svelte:fragment>
	<svelte:fragment slot="footer">
		<div class="d-flex justify-content-end">
			<ButtonV2
				variant="secondary"
				on:click={closeModal}
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				responsiveSize={{
					sm: 'm',
				}}
			>
				{$format('button_cancel', { default: 'Cancel' })}
			</ButtonV2>
			<ButtonV2
				on:click={removeImage}
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				variant="danger"
				responsiveSize={{
					sm: 'm',
				}}
			>
				{$format('delete_image_button', { default: 'Delete Image' })}
			</ButtonV2>
		</div>
	</svelte:fragment>
</Modal>
